import {useCallback, useRef, useState} from "react";
import {AxiosError} from "axios";
import {useTypedSelector} from "../../redux/Hooks/storeSelectors";
import {
    selectCurrentProjectId,
    selectEntity,
} from "../../redux/selectors/selectors";
import {isErrorResponse, isRestApiErrorResponse} from "../../utils/utils";
import {MetadataPropertyEntity} from "../../api/entities/replancity_MetadataProperty";
import {projectsApi} from "../../api/projectsApi";
import {BaseEntity, BaseEntityNameLess} from "../../api/entities/BaseEntity";
import {setEntity} from "../../redux/entity/entity-reducer";
import {entityApi, EntityView, SaveEntityProps} from "../../api/entityApi";
import {useDispatch} from "react-redux";
import {useToastContext} from "../../context/toastContext";
import {useTranslation} from "react-i18next";
import {roadManagementApi} from "../../api/roadManagementApi";
import {DataById} from "../../redux/map/types";
import {usePresetsContext} from "../../context/presetsContext";
import {EntityFormMetadataProperty} from "../../components/Entities/EntityForm/EntityForm";


export type UseEntityLoaderProps = {
    entityName: string;
    saveFnArgs?: (entity: any) => ({ [key: string]: any });
    presetProperty?: string;
    entityView?: EntityView;
}

export type EntityMetadata = {
    visibleMetadata: EntityFormMetadataProperty[];
    metadataWithSaveAndUpdateOnChange: { [key: string]: boolean };
}

export type EntityLoaderReturnType<T extends BaseEntity | BaseEntityNameLess> = {
    loading: boolean;
    entity: T;
    metadata: EntityMetadata | undefined;
    loadData: ({entityName, entityId, entityView}: {
        entityName: string;
        entityId?: string | null | undefined;
        entityView?: EntityView
    }) => Promise<void>;
    changeForm: (entity: any) => void;
    saveFn: ({entityName, id, data}: SaveEntityProps) => Promise<T>;
    groupSaveFn: any;
    deleteFn: ({entity}: { entity: T }) => Promise<any>;
    error: AxiosError | undefined;
}

const createShowIfFunction = function (fieldName: string, likeShowIfProp?: string) {
    if (likeShowIfProp) {
        try {
            return new Function(`return ${likeShowIfProp};`);
        } catch (e) {
            console.error(`Corrupted ${likeShowIfProp}: "${likeShowIfProp}" for "${fieldName}" metadata\n`, e);
            return () => false;
        }
    }
    return () => false;
}

export const getVisibleEntityMetadata = (metaData: MetadataPropertyEntity[]): {
    metadata: {
        visibleMetadata: EntityFormMetadataProperty[],
        metadataWithSaveAndUpdateOnChange: { [key: string]: boolean }
    },
    entityDraft: DataById<unknown>,
} => {
    const entityDraft = {};
    const metadataWithSaveAndUpdateOnChange = {};
    const visibleMetadata: EntityFormMetadataProperty[] =
        metaData
            .filter(({
                         name,
                         type,
                         showIf,
                         defaultValue,
                         saveAndUpdateOnChange,
                         options
                     }) => {
                if (!!showIf && showIf !== 'false') {
                    entityDraft[name] = '';
                    if (options.length) {
                        entityDraft[name] = type === 'Enum' ? options[0].propertyName : options[0].id;
                    }
                    if (defaultValue) {
                        entityDraft[name] = defaultValue;
                    }
                    if (saveAndUpdateOnChange) {
                        metadataWithSaveAndUpdateOnChange[name] = true;
                    }
                }

                return !!showIf && showIf !== 'false';
            })
            .map(meta => ({
                ...meta,
                showIf: createShowIfFunction(meta.name, meta.showIf),
                mandatoryIf: createShowIfFunction(meta.name, meta.mandatoryIf)
            }));

    return {metadata: {visibleMetadata, metadataWithSaveAndUpdateOnChange}, entityDraft};
};

export const useEntityLoader = <T extends BaseEntityNameLess>({
                                                                  entityName,
                                                                  saveFnArgs,
                                                                  presetProperty,
                                                              }: UseEntityLoaderProps = {} as UseEntityLoaderProps): EntityLoaderReturnType<T> => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<AxiosError>();
    //TODO ideally local state should be used, but Redux is used by breadcrumbs nowadays
    // const [entity, setEntity] = useState<any>({});
    const entity = useTypedSelector((state) => selectEntity(state, entityName));
    const [metadata, setMetadata] = useState<EntityMetadata>();
    // const metadata = useTypedSelector<MetadataPropertyEntity[]>((state) => selectEntityMetadata(state, entityName));
    const projectId = useTypedSelector(selectCurrentProjectId);
    const {selectedPresetId} = usePresetsContext();
    const abortControllerRef = useRef(new AbortController());
    const dispatch = useDispatch();
    const {addToast} = useToastContext();
    const {t} = useTranslation();

    const loadMetadata = useCallback(async (entityName: string, selectedPresetId: string): Promise<MetadataPropertyEntity[]> => {
        return await projectsApi.fetchEntityMetadata({
            entityName,
            presetId: selectedPresetId,
            abortSignal: abortControllerRef.current.signal
        });
    }, [])

    const loadEntity = useCallback(async ({entityName, entityId, entityView}: {
        entityName: string;
        entityId: string;
        entityView?: EntityView
    }): Promise<T> => {
        // try {
        const resp = await entityApi.getEntity<T>({
            entityId: entityId,
            entityName,
            ...(
                entityView ?
                    {
                        params: {
                            view: entityView
                        }
                    }
                    : {}
            ),
            abortSignal: abortControllerRef.current.signal
        });

        //TODO call flyTo util? example was useRoadSegmentLoader

        return resp;
    }, [])

    const saveFn = useCallback(async ({entityName, id, data: entity}: SaveEntityProps): Promise<T> => {
        setLoading(true);

        const resp = await entityApi.saveEntity<T>({
            id,
            entityName,
            data: {
                ...(presetProperty ?
                    {
                        [presetProperty]: {
                            id: selectedPresetId
                        }
                    } : {}),
                ...entity,
                ...saveFnArgs?.(entity),
            },
            abortSignal: abortControllerRef.current.signal
        });

        setLoading(false);

        return resp;
    }, [projectId, selectedPresetId, saveFnArgs])


    //TODO maybe move somewhere
    const groupSaveFn = useCallback(async ({
                                               ids,
                                               data: roadSegment,
                                               entityName
                                           }) => {
        const resp = await roadManagementApi.groupUpdate(
            {
                roadInfrastructureUpdateTask: {
                    source: {...roadSegment},
                    targets: ids
                } as any
            },
            abortControllerRef.current.signal
        );

        //TODO currently responses is empty => remove below?
        // if (!isErrorResponse(resp)) {
        //     // setData(resp as any);
        //     dispatch(setEntity(entityName, resp));
        //     dispatch(clearedSelectedMapFeatures());
        // }

        return resp;
    }, [])

    const deleteFn = useCallback(async ({entity}: { entity: T }) => {
        setLoading(true);

        const resp = await entityApi.deleteEntity({
            entityId: entity.id,
            entityName,
            abortSignal: abortControllerRef.current.signal
        });
        if (!isErrorResponse(resp)) {
            addToast(`${t('common.delete-successful')}`, 'success');
        }
        setLoading(false);

        return resp;
    }, [entityName])

    const loadData = useCallback(async ({entityName, entityId, entityView}: {
        entityName: string;
        entityId?: string | null | undefined;
        entityView?: EntityView
    }): Promise<void> => {
        setLoading(true);

        const [_metaData, entity] = await Promise.all([
            loadMetadata(entityName, selectedPresetId),
            ...(entityId ? [loadEntity({
                entityName,
                entityId,
                entityView
            })] : [])])

        if (isErrorResponse(_metaData) || isRestApiErrorResponse(_metaData)) {
            console.error('Replan. useEntityLoader.loadMetadata error:', JSON.stringify(metadata));
            setError(_metaData as any);

            setLoading(false);
            return;
        }

        if (isErrorResponse(entity) || isRestApiErrorResponse(entity)) {
            console.error('Replan. useEntityLoader.loadEntity error:', entity);
            setError(entity as any);

            setLoading(false);
            return;
        }

        if (_metaData) {
            const {metadata, entityDraft} = getVisibleEntityMetadata(_metaData);

            setMetadata(metadata);
            // dispatch(setEntityMetadata({entityName, metadata: visibleMetadata}));

            // setEntity({
            //     ...entityDraft,
            //     ...(entityId ? entity : {})
            // });
            dispatch(setEntity({
                entityName,
                entity: {
                    ...entityDraft,
                    ...(entityId ? entity : {})
                }
            }));
        }

        setLoading(false);
    }, [loadMetadata, loadEntity, selectedPresetId]);

    const changeForm = useCallback((formDataObj: { [key: string]: string | number }) => {
        dispatch(setEntity({entityName, entity: {...formDataObj}}));
    }, [entity])

    return {
        loading,
        entity: entity as T ?? {},
        metadata,
        loadData,
        changeForm,
        saveFn,
        groupSaveFn,
        deleteFn,
        error
    };
};
